// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import FundInvestmentInvestor from 'FundInvestmentData';
import FundOpportunityChatResponse from 'FundOpportunityChatResponse';
import FundOpportunity from '../../models/FundOpportunity';
import Investor from '../../models/Investor';
import { NOT_FOUND } from '../../services/BaseBackendService';
import type { OpportunityStatus } from '../../services/BaseBackendService';
export const adminBackendServiceFundOpportunityMixin = {
  triggerDocumentUpdated(
    fundOpportunity: FundInvestmentInvestor | FundOpportunity,
    docs: { pdf?: boolean; contentManagement?: boolean; content?: string },
  ): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `fundOpportunities/${fundOpportunity.id}/refreshGeminiDocuments`,
      'POST',
      docs,
      () => undefined,
    );
  },

  updateFundOpportunity(
    id: string,
    formFields: {
      name: string | null;
      fundSize: number | null;
      fundDuration: number | null;
      interest: number | null;
      lga: string | null;
      returnOnInvestment: number | null;
      siteArea: number | null;
      currentValuation?: number | null;
      seniorDebtLvr?: number | null;
      closeDate?: string | null;
      security?: 'REGISTERED_MORTGAGE' | 'UNREGISTERED_MORTGAGE' | null;
      capitalType?: 'SENIOR_DEBT' | 'JUNIOR_DEBT' | 'PREFERRED_EQUITY' | null;
      sector?: Array<
        | 'RESIDENTIAL_LAND'
        | 'RESIDENTIAL_BUILT_FORM'
        | 'INDUSTRIAL'
        | 'OFFICE'
        | 'RETAIL'
        | 'ALTERNATIVE_OTHER'
      > | null;
      interestType?: 'SERVICING_FULL' | 'SERVICING_PARTIAL' | 'CAPITALISING' | null;
      facilityLimit?: number | null;
      summaryTitle?: string | null;
      excludeFromCustodian?: boolean;
    },
  ): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `fundOpportunities/${id}`,
      'PATCH',
      formFields,
      () => undefined,
    );
  },

  handlePromotionResponse(response: Response, resolve: () => void, reject: (reason?: any) => void) {
    if (response.ok) {
      resolve();
    } else {
      this.handleErrorResponse(response, reject);
    }
  },

  handleErrorResponse(response: Response, reject: (reason?: any) => void): void {
    {
      response
        .clone()
        .json()
        .then((json) => {
          const errorHandled = this.handleError(json);
          reject(errorHandled);
        })
        .catch(() => {
          return response.text().then((text) => {
            reject(text);
          });
        });
    }
  },

  authApiMutatePromotion(path: string, method: 'PATCH' | 'POST', body?: unknown): Promise<void> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL(path), {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
      await new Promise<void>((resolve, reject) => {
        this.handlePromotionResponse(response, resolve, reject);
      });
    });
  },

  approveFundOpportunity(id: string): Promise<void> {
    return this.authApiMutatePromotion(`fundOpportunities/${id}/approve`, 'PATCH');
  },

  promoteFundOpportunityToEOI(
    id: string,
    overrides?: {
      overridePDF?: boolean;
      overrideMissingIrrUpdate?: boolean;
      overrideMissingSummaryImage?: boolean;
    },
  ): Promise<void> {
    return this.authApiMutatePromotion(`fundOpportunities/${id}/promoteToEOI`, 'PATCH', {
      overridePDF: overrides?.overridePDF ?? false,
      overrideMissingIrrUpdate: overrides?.overrideMissingIrrUpdate ?? false,
      overrideMissingSummaryImage: overrides?.overrideMissingSummaryImage ?? false,
    });
  },

  checkFundOpportunityPDFAvailable(id: string): Promise<boolean> {
    return this.authApiGetAuthed(
      `fundOpportunities/${id}/pdfAvailable`,
      'Failed to check PDF availability',
      (data) => data.available as boolean,
    );
  },

  promoteFundOpportunityToApplicationOpen(id: string): Promise<void> {
    return this.authApiMutatePromotion(`fundOpportunities/${id}/promoteToApplicationOpen`, 'PATCH');
  },

  promoteFundOpportunityToUnderManagement(
    id: string,
    overrideMissingCommittedInvestments: boolean = false,
  ): Promise<void> {
    return this.authApiMutatePromotion(
      `fundOpportunities/${id}/promoteToUnderManagement`,
      'PATCH',
      {
        overrideMissingCommittedInvestments,
      },
    );
  },

  promoteFundOpportunityToComplete(id: string): Promise<void> {
    return this.authApiMutatePromotion(`fundOpportunities/${id}/promoteToComplete`, 'PATCH');
  },

  getFundInvestmentData(fundLeadOpportunityId: string): Promise<{
    opportunity: FundOpportunity;
    investors: FundInvestmentInvestor[];
  }> {
    return this.authFetchPromise(async (token) => {
      const oppResponse = await this.authenticatedFetch(
        this.apiURL(`fundOpportunities/${fundLeadOpportunityId}/withInvestmentData`),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const oppData = await oppResponse.json();
      const fundOpportunity = oppData.data;

      const investmentResponse = await this.authenticatedFetch(
        this.apiURL(`fundOpportunities/${fundLeadOpportunityId}/investors`),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const investmentData = await investmentResponse.json();
      return {
        opportunity: fundOpportunity,
        investors: investmentData.data.map((i: any) => {
          const baseData = { ...i };
          ['indicativeInvestment', 'preferredInvestment', 'committedInvestment'].forEach((inv) => {
            if (baseData[inv]) {
              baseData[inv] = parseInt(baseData[inv], 10);
            }
          });
          return baseData;
        }) as FundInvestmentInvestor[],
      };
    });
  },

  getFirstInvestorForFund(fundOpportunityId: string): Promise<Investor | null> {
    return this.authApiGetAuthed<Investor[]>(
      `fundOpportunities/${fundOpportunityId}/investors`,
      'Network error while fetching fund investors',
      (json) => json.data as Investor[],
    )
      .then((investors) => (investors.length > 0 ? investors[0] : null))
      .catch(() => null);
  },

  getInvestor(id: string): Promise<Investor> {
    return this.authApiGetAuthed(
      `investors/${id}`,
      'Network error while fetching investor',
      (data) => data.data as Investor,
    );
  },

  createFundOpportunity(name: string, summaryTitle: string): Promise<string> {
    return this.authApiMutateAuthed(
      'fundOpportunities',
      'POST',
      { name, summaryTitle },
      (data) => data.data.id as string,
    );
  },

  // Deliberately does its own fetch: authApiMutateAuthed's non-2xx handling
  // only surfaces `error` (a string), dropping the Zod `details` array the
  // /complete endpoint returns for validation failures — details the AI chat
  // needs to ask the user for the specific fields it got wrong.
  createCompleteFundOpportunity(data: FundOpportunityChatResponse['currentData']): Promise<string> {
    const creationData = Object.fromEntries(
      Object.entries(data!).map(([key, value]) => {
        // Handle tempFundOpportunityId specially since it's not an AttemptedField
        if (key === 'tempFundOpportunityId') {
          return [key, value];
        }

        if (value && typeof value === 'object' && 'value' in value) {
          return [key, (value as any).value];
        }

        return [key, value];
      }),
    );

    // Remove createdViaAI from data sent to backend
    delete creationData.createdViaAI;

    return this.authFetchPromise(async (token: string | null) => {
      const response = await this.authenticatedFetch(this.apiURL('fundOpportunities/complete'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(creationData),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        const validationError = new Error(body?.error || `Error: ${response.status}`);
        (validationError as any).details = Array.isArray(body?.details) ? body.details : [];
        throw validationError;
      }

      return body.data.id as string;
    });
  },

  getFundOpportunityByStatus(
    status: OpportunityStatus | OpportunityStatus[],
  ): Promise<FundOpportunity[]> {
    return this.authFetchPromise(async (token) => {
      const fundOpportunityResult: FundOpportunity[] = [];
      for (const s of [status].flat()) {
        const response = await this.authenticatedFetch(
          this.apiURL(`fundOpportunities/byStatus/${s}`),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const responseData = await response.json();
        responseData.data.forEach((d: FundOpportunity) => fundOpportunityResult.push(d));
      }
      return fundOpportunityResult;
    });
  },

  getPagedFundOpportunitiesByStatus(
    status: OpportunityStatus,
    page: number = 1,
    limit: number = 20,
  ): Promise<{
    data: FundOpportunity[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      hasMore: boolean;
    };
  }> {
    return this.authApiGetAuthed(
      `fundOpportunities/byStatus/${status}?page=${page}&limit=${limit}`,
      'Network error while fetching fund opportunities',
      (json) => json,
    );
  },

  deleteFundOpportunity(id: string, force: boolean = false): Promise<void> {
    return this.authFetchPromise(async (token) => {
      const url = force
        ? this.apiURL(`fundOpportunities/${id}?force=true`)
        : this.apiURL(`fundOpportunities/${id}`);
      const response = await this.authenticatedFetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 204) return;
      if (response.status === 409) {
        const warning = await response.json();
        throw { type: 'warning', data: warning };
      }
      throw this.handleError(await response.json());
    });
  },

  cloneFundOpportunity(
    sourceId: string,
    request: CloneFundOpportunityRequest,
  ): Promise<CloneFundOpportunityResponse> {
    return this.authApiMutateAuthed(
      `fundOpportunities/${sourceId}/clone`,
      'POST',
      request,
      (json) => json.data as CloneFundOpportunityResponse,
    );
  },

  linkFundOpportunity(sourceId: string, destinationId: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `fundOpportunities/${sourceId}/linkTo/${destinationId}`,
      'POST',
      undefined,
      () => undefined,
    );
  },
} satisfies ThisType<BaseBackendService>;
